import { useState } from 'react';
import { sendContactRequest } from '../../services/portfolio.service';
import { motion } from 'framer-motion';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

import mascot from '../../assets/contactus_mascot.PNG';

const calendarUrl = process.env.REACT_APP_GOOGLE_CALENDAR_URL;

export default function ContactUs() {
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phonenumber: '',
    about: '',
    file: null,
  });
  const [filePreview, setFilePreview] = useState(null);
  const [successMessage, setSuccessMessage] = useState();
  const [error, setError] = useState();

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      const file = files[0];
      setFormData({ ...formData, [name]: file });
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => setFilePreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSuccessMessage();
    setError();

    const formDataToSend = new FormData();
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone_number', formData.phonenumber);
    formDataToSend.append('full_name', formData.fullname);
    formDataToSend.append('description', formData.about);
    if (formData.file) {
      formDataToSend.append('attachment', formData.file);
    }

    try {
      const response = await sendContactRequest(formDataToSend);
      setSuccessMessage('Your request has been submitted successfully!');
      console.log(response.data);
    } catch (err) {
      setError('An error occurred while submitting your request.');
      console.error(err);
    }
  };

  return (
    <div className="w-full bg-white pb-24 pt-28 sm:pt-36">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 sm:p-12"
        >
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-secondary/25 blur-3xl" />

          <div className="relative flex flex-col items-center gap-8 text-center lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:text-left">
            <div>
              <div className="mb-4 flex items-center justify-center gap-2 lg:justify-start">
                <span className="h-2 w-2 flex-none rounded-sm bg-secondary" aria-hidden="true" />
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary-dark">Get in touch</p>
              </div>
              <h1 className="text-4xl font-semibold tracking-tight text-gray-950 sm:text-5xl">
                Let's build something together.
              </h1>
              <p className="mt-4 max-w-md text-lg text-gray-600">
                Tell us about your project, or grab a slot on our calendar for a live conversation.
              </p>
            </div>

            <img
              src={mascot}
              alt="Afroawi mascot holding a notebook"
              className="h-56 w-auto flex-none object-contain sm:h-64"
            />
          </div>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_22rem] lg:items-start">
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onSubmit={handleSubmit}
            className="rounded-lg border border-gray-200 bg-white p-6 sm:p-10"
          >
            <div className="grid grid-cols-1 gap-y-6">
              {successMessage && (
                <div className="rounded-lg bg-green-600 px-3 py-2 text-white">
                  <p>{successMessage}</p>
                </div>
              )}
              {error && (
                <div className="rounded-lg bg-red-600 px-3 py-2 text-white">
                  <p>{error}</p>
                </div>
              )}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="fullname" className="block text-sm font-medium text-gray-900">
                    Full Name
                  </label>
                  <div className="mt-2">
                    <input
                      id="fullname"
                      name="fullname"
                      type="text"
                      placeholder="Full Name"
                      value={formData.fullname}
                      onChange={handleChange}
                      required
                      autoComplete="fullname"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/40 sm:text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                    Email
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/40 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="phonenumber" className="block text-sm font-medium text-gray-900">
                    Phone Number
                  </label>
                  <div className="mt-2">
                    <input
                      id="phonenumber"
                      name="phonenumber"
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phonenumber}
                      onChange={handleChange}
                      required
                      autoComplete="tel"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/40 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col justify-end">
                  <label htmlFor="file" className="block text-sm font-medium text-gray-900">
                    Attach a file (PDF or image)
                  </label>
                  <div className="mt-2 flex items-center gap-4">
                    <input
                      id="file"
                      name="file"
                      type="file"
                      accept=".pdf, .jpg, .png"
                      onChange={handleChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:rounded-lg file:border-0 file:bg-primary
                        file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white
                        hover:file:bg-primary/90
                      "
                    />
                    {filePreview && (
                      <div className="flex-shrink-0">
                        <img src={filePreview} alt="Preview" className="h-10 w-10 rounded-lg object-cover ring-1 ring-gray-200" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="about" className="block text-sm font-medium text-gray-900">
                  Tell us about your project
                </label>
                <div className="mt-2">
                  <textarea
                    id="about"
                    name="about"
                    placeholder="What are you looking to build?"
                    value={formData.about}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/40 sm:text-sm"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="mt-8 w-full rounded-lg border border-primary bg-primary px-4 py-3 font-semibold text-white transition-colors duration-300 hover:bg-primary/90"
            >
              Submit Request
            </button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="rounded-lg border border-gray-200 p-6 lg:sticky lg:top-24"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-secondary/40 text-secondary-dark">
              <CalendarDaysIcon className="h-5 w-5" aria-hidden="true" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-gray-950">Prefer to talk live?</h2>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              Book a free 30-minute call directly on our calendar — no back-and-forth emails needed.
            </p>

            {calendarUrl ? (
              <div className="mt-5 overflow-hidden rounded-lg border border-gray-200">
                <iframe
                  src={calendarUrl}
                  title="Book a call with Afroawi"
                  style={{ border: 0 }}
                  width="100%"
                  height="500"
                  frameBorder="0"
                />
              </div>
            ) : (
              <a
                href="mailto:afroawi.tech@gmail.com"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary bg-primary px-4 py-2.5 text-sm font-semibold text-white transition duration-200 hover:bg-primary/90"
              >
                Schedule a call
              </a>
            )}
          </motion.aside>
        </div>
      </div>
    </div>
  );
}
